import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import {
	WithStyles,
	Theme,
	createStyles,
	withStyles,
	ListItemSecondaryAction
} from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";

import { Meal } from '../../api/meals';

interface State {
	adding: boolean;
	editing: boolean;
	deleting: boolean;
	dialogID: string;
	dialogName: string;
	dialogInfo: string;
}

export interface MealListProps {
	meals: Meal[];
	onAdd: (meal: Meal) => void;
	onEdit: (meal: Meal) => void;
	onDelete: (id: string) => void;
}

type Props =
	MealListProps &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps> &
	WithStyles<typeof styles> &
	InjectedTranslateProps
;

export class MealList extends React.Component<Props, State> {
	public readonly state: State = {
		adding: false,
		editing: false,
		deleting: false,
		dialogID: "",
		dialogName: "",
		dialogInfo: "",
	};

	private getMealById = (id: string) => {
		return this.props.meals.find(m => m.id === id);
	};

	private handleAddClick = () => {
		this.setState({
			adding: true,
			dialogID: "",
			dialogName: "",
			dialogInfo: ""
		});
	};

	private handleEditClick = (id: string) => {
		const meal = this.getMealById(id);

		this.setState({
			editing: true,
			dialogID: id,
			dialogName: meal.name,
			dialogInfo: meal.info
		});
	};

	private handleDeleteClick = (id: string) => {
		const meal = this.getMealById(id);

		this.setState({
			deleting: true,
			dialogID: id,
			dialogName: meal.name,
			dialogInfo: meal.info
		});
	};

	private handleDialogCancelClick = () => {
		this.setState({
			adding: false,
			editing: false,
			deleting: false
		});
	};

	private handleDialogNameChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		this.setState({
			dialogName: event.currentTarget.value
		});
	};

	private handleDialogInfoChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		this.setState({
			dialogInfo: event.currentTarget.value
		});
	};

	private handleAddSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		this.props.onAdd({
			id: null,
			name: this.state.dialogName,
			info: this.state.dialogInfo
		});

		this.handleDialogCancelClick();
	};

	private handleEditSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		this.props.onEdit({
			id: this.state.dialogID,
			name: this.state.dialogName,
			info: this.state.dialogInfo
		});

		this.handleDialogCancelClick();
	};

	private handleDeleteSubmitClick = () => {
		this.props.onDelete(this.state.dialogID);

		this.handleDialogCancelClick();
	};

	private renderMealList() {
		const { classes, t, meals } = this.props;
		const {
			adding,
			editing,
			deleting,
			dialogName,
			dialogInfo
		} = this.state;

		if (meals.length === 0) {
			return (
				<ListItem>
					<ListItemText className={classes.notAvailableItem}>
						Noch keine Speisen vorhanden
					</ListItemText>
				</ListItem>
			);
		}

		return meals.map(meal => (
			<ListItem key={meal.id}>
				<ListItemText>{meal.name}</ListItemText>
				<ListItemSecondaryAction>
					<IconButton
						onClick={() => this.handleEditClick(meal.id)}
					>
						<EditIcon />
					</IconButton>
					<IconButton
						onClick={() => this.handleDeleteClick(meal.id)}
					>
						<DeleteIcon />
					</IconButton>
				</ListItemSecondaryAction>
			</ListItem>
		));
	}

	public render() {
		const { classes, t, meals } = this.props;
		const {
			adding,
			editing,
			deleting,
			dialogName,
			dialogInfo
		} = this.state;

		return (
			<Paper>
				<List>{this.renderMealList()}</List>
				<div className={classes.buttonContainer}>
					<Button onClick={this.handleAddClick} variant="outlined">
						Hinzufügen
					</Button>
				</div>
				<Dialog open={adding}>
					<form onSubmit={this.handleAddSubmit}>
						<DialogTitle>Neue Speise hinzufügen</DialogTitle>
						<DialogContent>
							<TextField
								label="Name"
								name="name"
								type="text"
								value={dialogName}
								onChange={this.handleDialogNameChange}
								fullWidth
								required
								autoComplete="off"
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={this.handleDialogCancelClick}>
								Abbrechen
							</Button>
							<Button type="submit" color="primary">
								Hinzufügen
							</Button>
						</DialogActions>
					</form>
				</Dialog>
				<Dialog open={editing}>
					<form onSubmit={this.handleEditSubmit}>
						<DialogTitle>Speise bearbeiten</DialogTitle>
						<DialogContent>
							<TextField
								label="Name"
								type="text"
								value={dialogName}
								onChange={this.handleDialogNameChange}
								fullWidth
								required
								autoComplete="off"
							/>
							<TextField
								label="Info"
								type="text"
								value={dialogInfo}
								onChange={this.handleDialogInfoChange}
								fullWidth
								required
								autoComplete="off"
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={this.handleDialogCancelClick}>
								Abbrechen
							</Button>
							<Button type="submit" color="primary">
								Speichern
							</Button>
						</DialogActions>
					</form>
				</Dialog>
				<Dialog open={deleting}>
					<DialogTitle>Speisen löschen</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Speisen <strong>{dialogName}</strong> wirklich
							löschen?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={this.handleDialogCancelClick}>
							Abbrechen
						</Button>
						<Button
							onClick={this.handleDeleteSubmitClick}
							color="primary"
						>
							Löschen
						</Button>
					</DialogActions>
				</Dialog>
			</Paper>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({});

const mapDispatchToProps = (state: StoreState) => ({});

const styles = (theme: Theme) =>
	createStyles({
		buttonContainer: {
			display: "flex",
			justifyContent: "flex-end",
			padding: 20
		},
		textarea: {
			marginTop: 20
		},
		notAvailableItem: {
			color: "#999",
			fontStyle: "italic"
		}
	});

export const ConnectedMealList = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(MealList)
	)
);
